import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import Image from "next/image";

const Footer = () => {
  const currentYear = 2026;

  const footerLinks = {
    services: [
      { id: "footer_service_construction", label: "House Construction", href: "/services#construction" },
      { id: "footer_service_land", label: "Land for Sale", href: "/services#land" },
      { id: "footer_service_materials", label: "Building Materials", href: "/services#materials" },
      { id: "footer_service_endtoend", label: "End-to-End Service", href: "/services#endtoend" },
    ],
    projects: [
      { id: "footer_project_sonarbangla", label: "Sonarbangla, Joka", href: "/projects#sonarbangla" },
      { id: "footer_project_jokametro", label: "Near Joka Metro", href: "/projects#jokametro" },
    ],
    company: [
      { id: "footer_company_about", label: "About Us", href: "/about" },
      { id: "footer_company_founder", label: "Our Founder", href: "/about#founder" },
      { id: "footer_company_values", label: "Our Values", href: "/about#values" },
    ],
    contact: [
      { id: "footer_contact_page", label: "Contact Us", href: "/contact" },
      { id: "footer_contact_phone", label: "+91 7890369000", href: "tel:+917890369000" },
      { id: "footer_contact_email", label: "sales@artienterprises.in", href: "mailto:sales@artienterprises.in" },
    ],
  };

  const socialLinks = [
    { id: "social_whatsapp", name: "ChatBubbleLeftRightIcon", href: "https://wa.link/yurmqd", label: "WhatsApp" },
    { id: "social_facebook", name: "GlobeAltIcon", href: "https://facebook.com", label: "Facebook" },
    { id: "social_instagram", name: "CameraIcon", href: "https://instagram.com", label: "Instagram" },
  ];

  return (
    <footer className="bg-foreground text-white py-16 border-t border-gray-700">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <Image
                src="/assets/images/Website_logo-1769951970593.png"
                alt="ARTI Enterprise Logo"
                width={56}
                height={56}
                className="h-14 w-14 object-contain brightness-0 invert"
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white leading-tight">ARTI Enterprise</span>
                <span className="text-sm text-gray-300">Foundation Se Finishing Tak!</span>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-6 max-w-sm">
              Building affordable homes for Kolkata's middle-class families since 2002. 
              Honest construction, durable quality, and trusted service — only in Kolkata.
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.id}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-primary hover:border-primary transition-colors"
                  aria-label={social.label}
                >
                  <Icon name={social.name as any} size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-white mb-4">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.id}>
                  <Link
                    href={link.href}
                    className="text-sm text-gray-300 hover:text-secondary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Projects Column */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-white mb-4">
              Properties
            </h4>
            <ul className="space-y-3">
              {footerLinks.projects.map((link) => (
                <li key={link.id}>
                  <Link
                    href={link.href}
                    className="text-sm text-gray-300 hover:text-secondary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="text-sm font-bold uppercase tracking-widest text-white mb-4 mt-8">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.id}>
                  <Link
                    href={link.href}
                    className="text-sm text-gray-300 hover:text-secondary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-white mb-4">
              Contact
            </h4>
            <ul className="space-y-3">
              {footerLinks.contact.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    className="text-sm text-gray-300 hover:text-secondary transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6 p-4 bg-white/5 border border-white/10 rounded-lg">
              <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">
                Office Address
              </p>
              <p className="text-sm text-gray-300 leading-relaxed">
                10/19/4, Yani Sarani<br />
                Joka, Kolkata – 700104
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-xs text-gray-400">
            © {currentYear} ARTI Enterprise. All rights reserved.
          </p>
          <p className="text-xs text-gray-400 uppercase tracking-wider">
            Serving only Kolkata
          </p>
          <div className="flex items-center gap-6 text-xs text-gray-400">
            <Link href="#" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link href="#" className="hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>

        {/* Website Credit */}
        <div className="pt-4 text-center">
          <p className="text-xs text-gray-400">
            © Website made by{" "}
            <a
              href="https://www.thesilverstonemedia.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary hover:text-white transition-colors underline"
            >
              The Silverstone Media
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;